/**
 * Canvas silhouettes.
 *
 * Every figure is authored in a normalised frame: the origin sits between the
 * feet, +u is right, +v is up, and 1.0 unit is the figure's full height. A
 * caller only has to know where on screen it goes and how tall it should be,
 * which is what makes one drawing usable at 40px and at 400px.
 *
 * Two things learned the hard way and worth keeping written down:
 *
 *  · Proportion is the whole game. A silhouette reads as a person or as a
 *    robot almost entirely on head-to-shoulder ratio — an early version had
 *    shoulders 40% as wide as the figure was tall and looked like a filing
 *    cabinet. Roughly seven-and-a-half heads to the body is the target.
 *
 *  · Never put eyes on a silhouette. Two dots read as a cartoon face, and so
 *    does a lit arc round the head. If a figure needs to be *doing* something,
 *    give it a gesture — a raised arm reads instantly and cannot be misread.
 */

/** Map normalised (u, v) to canvas coordinates for a figure of height `h`. */
function frame(ctx, x, groundY, h) {
  const X = (u) => x + u * h;
  const Y = (v) => groundY - v * h;
  return {
    X,
    Y,
    move: (u, v) => ctx.moveTo(X(u), Y(v)),
    line: (u, v) => ctx.lineTo(X(u), Y(v)),
    curve: (u1, v1, u2, v2, u3, v3) =>
      ctx.bezierCurveTo(X(u1), Y(v1), X(u2), Y(v2), X(u3), Y(v3)),
    ellipse: (u, v, ru, rv, rot = 0) =>
      ctx.ellipse(X(u), Y(v), ru * h, rv * h, rot, 0, Math.PI * 2),
  };
}

/** A robed figure — the ordinary person, and the size reference. */
export function robed(ctx, x, groundY, h, options = {}) {
  const f = frame(ctx, x, groundY, h);
  const lean = options.lean ?? 0;
  const w = options.width ?? 1;

  ctx.beginPath();
  f.move(-0.052 * w + lean, 0.855);
  f.curve(-0.115 * w + lean, 0.845, -0.15 * w, 0.805, -0.156 * w, 0.72);
  f.curve(-0.168 * w, 0.52, -0.186 * w, 0.27, -0.198 * w, 0.012);
  f.curve(-0.1 * w, -0.012, 0.1 * w, -0.012, 0.198 * w, 0.012);
  f.curve(0.186 * w, 0.27, 0.168 * w, 0.52, 0.156 * w, 0.72);
  f.curve(0.15 * w, 0.805, 0.115 * w + lean, 0.845, 0.052 * w + lean, 0.855);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  f.ellipse(lean * 1.3, 0.93, 0.055, 0.066);
  ctx.fill();

  if (options.headdress !== false) {
    ctx.beginPath();
    f.move(-0.07 + lean * 1.3, 0.905);
    f.curve(-0.074 + lean * 1.3, 0.985, 0.074 + lean * 1.3, 0.985, 0.07 + lean * 1.3, 0.905);
    f.curve(0.086 + lean, 0.87, 0.092 + lean, 0.84, 0.08 + lean, 0.822);
    f.curve(0.04 + lean, 0.855, -0.04 + lean, 0.855, -0.08 + lean, 0.822);
    f.curve(-0.092 + lean, 0.84, -0.086 + lean, 0.87, -0.07 + lean * 1.3, 0.905);
    ctx.closePath();
    ctx.fill();
  }
}

/** An armoured champion: helmet and crest, cuirass, shield, planted spear. */
export function warrior(ctx, x, groundY, h) {
  const f = frame(ctx, x, groundY, h);
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  ctx.save();
  ctx.globalAlpha = 0.92;
  ctx.beginPath();
  f.ellipse(0.175, 0.52, 0.098, 0.185, -0.12);
  ctx.fill();
  ctx.restore();

  ctx.beginPath();
  f.move(0.026, 0);
  f.line(0.026, 0.455);
  f.line(0.1, 0.455);
  f.curve(0.098, 0.3, 0.094, 0.14, 0.09, 0);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  f.move(-0.026, 0);
  f.line(-0.026, 0.455);
  f.line(-0.1, 0.455);
  f.curve(-0.098, 0.3, -0.094, 0.14, -0.09, 0);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  f.move(-0.046, 0.866);
  f.curve(-0.106, 0.854, -0.141, 0.828, -0.149, 0.792);
  f.curve(-0.141, 0.702, -0.13, 0.612, -0.121, 0.548);
  f.curve(-0.141, 0.505, -0.151, 0.472, -0.153, 0.436);
  f.line(0.153, 0.436);
  f.curve(0.151, 0.472, 0.141, 0.505, 0.121, 0.548);
  f.curve(0.13, 0.612, 0.141, 0.702, 0.149, 0.792);
  f.curve(0.141, 0.828, 0.106, 0.854, 0.046, 0.866);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  f.move(-0.128, 0.804);
  f.curve(-0.174, 0.784, -0.191, 0.722, -0.189, 0.642);
  f.curve(-0.187, 0.578, -0.181, 0.522, -0.173, 0.484);
  f.curve(-0.145, 0.488, -0.137, 0.522, -0.141, 0.568);
  f.curve(-0.147, 0.642, -0.145, 0.714, -0.117, 0.768);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = ctx.fillStyle;
  ctx.lineWidth = h * 0.013;
  ctx.beginPath();
  ctx.moveTo(f.X(-0.238), f.Y(1.16));
  ctx.lineTo(f.X(-0.196), f.Y(-0.01));
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(f.X(-0.24), f.Y(1.28));
  ctx.lineTo(f.X(-0.264), f.Y(1.15));
  ctx.lineTo(f.X(-0.214), f.Y(1.15));
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  f.move(-0.03, 0.858);
  f.line(0.03, 0.858);
  f.line(0.03, 0.892);
  f.line(-0.03, 0.892);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  f.ellipse(0, 0.925, 0.05, 0.055);
  ctx.fill();

  ctx.beginPath();
  f.move(-0.057, 0.922);
  f.curve(-0.06, 0.996, 0.06, 0.996, 0.057, 0.922);
  f.curve(0.062, 0.898, 0.058, 0.882, 0.048, 0.876);
  f.curve(0.03, 0.906, -0.03, 0.906, -0.048, 0.876);
  f.curve(-0.058, 0.882, -0.062, 0.898, -0.057, 0.922);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  f.move(-0.012, 0.986);
  f.curve(0.01, 1.058, 0.062, 1.076, 0.098, 1.044);
  f.curve(0.066, 1.04, 0.038, 1.01, 0.026, 0.978);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/* ── objects ────────────────────────────────────────────────────────────────
   These take their *named* dimension in pixels and derive the other from the
   real proportions in the verse, so the shape on screen is the shape the text
   describes rather than a generic box.                                       */

/** The ark: 300 long by 30 high, with the roof "finished to a cubit upward". */
export function ark(ctx, x, groundY, lengthPx) {
  const L = lengthPx;
  const hull = L * 0.1;
  ctx.beginPath();
  ctx.moveTo(x - L / 2, groundY - hull);
  ctx.lineTo(x + L / 2, groundY - hull);
  ctx.quadraticCurveTo(x + L * 0.44, groundY, x, groundY);
  ctx.quadraticCurveTo(x - L * 0.44, groundY, x - L / 2, groundY - hull);
  ctx.closePath();
  ctx.fill();
  ctx.fillRect(x - L * 0.3, groundY - hull * 2, L * 0.6, hull);
  ctx.fillRect(x - L * 0.16, groundY - hull * 2.7, L * 0.32, hull * 0.75);
}

/** A standing figure of gold, or a bronze pillar: ten times taller than wide. */
export function idol(ctx, x, groundY, heightPx) {
  const px = heightPx;
  const w = px * 0.1;
  ctx.fillRect(x - w * 1.4, groundY - px * 0.06, w * 2.8, px * 0.06);
  ctx.fillRect(x - w / 2, groundY - px * 0.86, w, px * 0.8);
  ctx.fillRect(x - w * 1.5, groundY - px * 0.8, w * 3, px * 0.055);
  ctx.beginPath();
  ctx.arc(x, groundY - px * 0.92, px * 0.062, 0, Math.PI * 2);
  ctx.fill();
}

/** A house, twice as long as it is high, with a doorway and columns. */
export function temple(ctx, x, groundY, lengthPx) {
  const L = lengthPx;
  const h = L * 0.5;
  ctx.fillRect(x - L / 2, groundY - h, L, h);
  ctx.beginPath();
  ctx.moveTo(x - L * 0.54, groundY - h);
  ctx.lineTo(x, groundY - h * 1.3);
  ctx.lineTo(x + L * 0.54, groundY - h);
  ctx.closePath();
  ctx.fill();
  const fill = ctx.fillStyle;
  ctx.fillStyle = 'rgba(8,6,10,.85)';
  ctx.fillRect(x - L * 0.06, groundY - h * 0.42, L * 0.12, h * 0.42);
  ctx.fillStyle = fill;
  for (let i = -2; i <= 2; i += 1) {
    ctx.fillRect(x + i * L * 0.17 - L * 0.012, groundY - h, L * 0.024, h);
  }
}

/** A chest with carrying poles, and two cherubim over the lid. */
export function chest(ctx, x, groundY, lengthPx) {
  const L = lengthPx;
  const h = L * 0.6;
  ctx.fillRect(x - L / 2, groundY - h, L, h * 0.82);
  ctx.fillRect(x - L * 0.56, groundY - h * 1.06, L * 1.12, h * 0.2);
  ctx.fillRect(x - L * 0.78, groundY - h * 0.62, L * 1.56, h * 0.08);
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(x + s * L * 0.24, groundY - h * 1.06);
    ctx.quadraticCurveTo(x + s * L * 0.34, groundY - h * 1.7, x + s * L * 0.06, groundY - h * 1.52);
    ctx.quadraticCurveTo(x + s * L * 0.22, groundY - h * 1.4, x + s * L * 0.16, groundY - h * 1.06);
    ctx.closePath();
    ctx.fill();
  }
}

/** The molten sea: a round bronze basin standing on oxen (1 Kings 7:25). */
export function basin(ctx, x, groundY, widthPx) {
  const D = widthPx;
  const h = D * 0.5;
  for (let i = -2; i <= 2; i += 1) {
    const ox = x + i * D * 0.19;
    ctx.fillRect(ox - D * 0.035, groundY - h * 0.42, D * 0.07, h * 0.42);
    ctx.beginPath();
    ctx.ellipse(ox, groundY - h * 0.48, D * 0.062, D * 0.036, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.moveTo(x - D / 2, groundY - h);
  ctx.bezierCurveTo(x - D * 0.47, groundY - h * 0.3, x - D * 0.3, groundY - h * 0.44, x, groundY - h * 0.46);
  ctx.bezierCurveTo(x + D * 0.3, groundY - h * 0.44, x + D * 0.47, groundY - h * 0.3, x + D / 2, groundY - h);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(x, groundY - h, D / 2, D * 0.085, 0, 0, Math.PI * 2);
  ctx.fill();
}

export const SHAPES = {
  warrior: (ctx, x, groundY, px) => warrior(ctx, x, groundY, px),
  ark,
  idol,
  temple,
  chest,
  basin,
};

/**
 * Width ÷ height for a `height` question, height ÷ length for a `length` one.
 *
 * Used to fit the view to the whole silhouette rather than to the one number
 * the question happens to ask about. Without this the ark — 450ft long and
 * 45ft tall — gets a camera framed for a 450ft-tall object, and the boat sits
 * as a sliver at the bottom of an empty screen.
 *
 * These are the *drawn* extents, not the scriptural ratio: Goliath's spear and
 * shield make him wider than a man, and the cherubim make the ark of the
 * covenant taller than its lid.
 */
export const ASPECT = {
  warrior: 0.62,
  ark: 0.3,
  idol: 0.32,
  temple: 0.68,
  chest: 1.05,
  basin: 0.62,
};
